import { useState, useEffect, useCallback } from 'react'
import { getProjectAudit, AuditEntry } from '../api'
import { Button } from '@/components/ui/button'
import { ArrowRight, Copy, PlusCircle, Pencil, Play, Square, Trash2, Clock } from 'lucide-react'

// Idea #85 (клиент totopseo, «Лог изменений проекта»): показывает
// КТО / КОГДА / КАКОЕ поле с ЧЕГО НА ЧТО изменил, а также создание копий и
// ручные запуски/остановки. Рядом с «Историей запусков» в нижней панели.

interface AuditHistoryProps {
  campaignId?: string
  mode?: 'booster' | 'profiler'
}

function formatDt(ts: number): string {
  const d = new Date(ts * 1000)
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${pad(d.getDate())}.${pad(d.getMonth() + 1)} ${pad(d.getHours())}:${pad(d.getMinutes())}`
}

function actionMeta(action: string): { icon: JSX.Element; cls: string } {
  switch (action) {
    case 'create':
      return { icon: <PlusCircle className="w-3.5 h-3.5" />, cls: 'text-emerald-500' }
    case 'copy':
      return { icon: <Copy className="w-3.5 h-3.5" />, cls: 'text-sky-500' }
    case 'start':
      return { icon: <Play className="w-3.5 h-3.5" />, cls: 'text-green-500' }
    case 'stop':
      return { icon: <Square className="w-3.5 h-3.5" />, cls: 'text-amber-500' }
    case 'delete':
      return { icon: <Trash2 className="w-3.5 h-3.5" />, cls: 'text-red-500' }
    case 'schedule':
      return { icon: <Clock className="w-3.5 h-3.5" />, cls: 'text-violet-500' }
    default:
      return { icon: <Pencil className="w-3.5 h-3.5" />, cls: 'text-muted-foreground' }
  }
}

function displayValue(v: string | null): string {
  if (v === null || v === undefined || v === '') return '—'
  return v
}

export default function AuditHistory({ campaignId, mode = 'booster' }: AuditHistoryProps) {
  const [items, setItems] = useState<AuditEntry[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)

  const load = useCallback(async () => {
    if (!campaignId) { setItems([]); setLoading(false); return }
    setLoading(true)
    try {
      const res = await getProjectAudit(mode, campaignId, 300, 0)
      setItems(res.items || [])
      setTotal(res.total || 0)
    } catch { /* ignore */ }
    setLoading(false)
  }, [campaignId, mode])

  useEffect(() => { load() }, [load])

  return (
    <div className="flex flex-col h-full">
      <div className="shrink-0 flex items-center gap-3 px-3 py-1.5 border-b border-border">
        <span className="text-xs font-medium text-foreground">
          История изменений
        </span>
        {total > 0 && (
          <span className="text-xs tabular-nums text-muted-foreground">{total}</span>
        )}
        <Button variant="ghost" size="sm" onClick={load} className="ml-auto h-7 text-xs gap-1">
          {'\u21BB'} Обновить
        </Button>
      </div>

      <div className="flex-1 overflow-y-auto">
        {loading ? (
          <div className="flex items-center justify-center py-8 text-muted-foreground">Загрузка...</div>
        ) : items.length === 0 ? (
          <div className="flex items-center justify-center py-8 text-xs text-muted-foreground">
            Пока нет изменений по проекту
          </div>
        ) : (
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-border">
                {['Дата', 'Кто', 'Действие', 'Что изменилось', ''].map((h, i) => (
                  <th key={i} className="text-left px-3 py-1.5 font-medium text-muted-foreground whitespace-nowrap">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {items.map(it => {
                const meta = actionMeta(it.action)
                const isUpdate = it.action === 'update' && it.field
                return (
                  <tr key={it.id} className="border-b border-border/60 hover:bg-accent/40 align-top">
                    <td className="px-3 py-1.5 tabular-nums text-muted-foreground whitespace-nowrap">
                      {formatDt(it.ts)}
                    </td>
                    <td className="px-3 py-1.5 text-foreground whitespace-nowrap max-w-[16ch] truncate" title={it.actor}>
                      {it.actor}
                    </td>
                    <td className="px-3 py-1.5 whitespace-nowrap">
                      <span className={`inline-flex items-center gap-1 ${meta.cls}`}>
                        {meta.icon}
                        {it.action_label || it.action}
                      </span>
                    </td>
                    <td className="px-3 py-1.5 text-foreground">
                      {isUpdate ? (
                        <div className="flex flex-wrap items-center gap-x-1.5 gap-y-0.5">
                          <span className="font-medium">{it.field_label}</span>
                          <span className="inline-flex items-center gap-1 text-muted-foreground">
                            <span
                              className="max-w-[24ch] truncate rounded bg-muted/60 px-1 py-px"
                              title={displayValue(it.old_value)}
                            >
                              {displayValue(it.old_value)}
                            </span>
                            <ArrowRight className="w-3 h-3 shrink-0" />
                            <span
                              className="max-w-[24ch] truncate rounded bg-primary/10 px-1 py-px text-foreground"
                              title={displayValue(it.new_value)}
                            >
                              {displayValue(it.new_value)}
                            </span>
                          </span>
                        </div>
                      ) : (
                        <span className="text-muted-foreground">{it.note || '\u2014'}</span>
                      )}
                    </td>
                    <td />
                  </tr>
                )
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  )
}
